
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { MapPin, Clock, DollarSign, Users, Award, Heart } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTopButton from "../components/BackToTopButton";
import FloatingSocialButtons from "../components/FloatingSocialButtons";

interface JobPosition {
  title: string;
  department: string;
  location: string;
  type: string;
  salary: string;
  description: string;
  requirements: string[];
  benefits: string[];
}

const CareersPage = () => {
  useEffect(() => {
    document.title = "Careers - DG IT Pvt. Ltd.";
    window.scrollTo(0, 0);
  }, []);

  const jobPositions: JobPosition[] = [
    {
      title: "Senior Graphic Designer",
      department: "Design",
      location: "Kathmandu, Nepal",
      type: "Full-time",
      salary: "NPR 40,000 - 60,000",
      description: "We're looking for a creative and experienced graphic designer to join our design team and create stunning visual content for our clients.",
      requirements: [
        "3+ years of graphic design experience",
        "Proficiency in Adobe Creative Suite",
        "Strong portfolio showcasing diverse design work",
        "Understanding of brand identity and visual communication",
        "Ability to work in a fast-paced environment"
      ],
      benefits: [
        "Competitive salary",
        "Health insurance",
        "Professional development opportunities",
        "Flexible working hours",
        "Creative work environment"
      ]
    },
    {
      title: "Video Editor",
      department: "Production",
      location: "Kathmandu, Nepal",
      type: "Full-time",
      salary: "NPR 35,000 - 50,000",
      description: "Join our video production team to create compelling video content for various clients across different industries.",
      requirements: [
        "2+ years of video editing experience",
        "Proficiency in Adobe Premiere Pro, After Effects",
        "Knowledge of color correction and audio editing",
        "Creative storytelling abilities",
        "Attention to detail and deadline management"
      ],
      benefits: [
        "Competitive salary",
        "Health insurance",
        "Latest editing software and equipment",
        "Training and skill development",
        "Collaborative team environment"
      ]
    },
    {
      title: "Content Creator",
      department: "Marketing",
      location: "Kathmandu, Nepal",
      type: "Full-time",
      salary: "NPR 30,000 - 45,000",
      description: "Create engaging content across multiple platforms to help our clients build their brand presence and connect with their audience.",
      requirements: [
        "1+ years of content creation experience",
        "Strong writing and communication skills",
        "Knowledge of social media platforms",
        "Basic understanding of SEO and digital marketing",
        "Creative thinking and trend awareness"
      ],
      benefits: [
        "Competitive salary",
        "Health insurance",
        "Social media training",
        "Growth opportunities",
        "Dynamic work culture"
      ]
    },
    {
      title: "Junior Web Developer",
      department: "Development",
      location: "Kathmandu, Nepal",
      type: "Full-time",
      salary: "NPR 25,000 - 40,000",
      description: "Join our development team to build modern, responsive websites and web applications using the latest technologies.",
      requirements: [
        "Basic knowledge of HTML, CSS, JavaScript",
        "Familiarity with React or other frameworks",
        "Understanding of responsive design",
        "Problem-solving skills",
        "Eagerness to learn and grow"
      ],
      benefits: [
        "Competitive salary",
        "Health insurance",
        "Mentorship and training",
        "Latest development tools",
        "Career advancement opportunities"
      ]
    }
  ];

  const companyValues = [
    {
      icon: <Users className="w-8 h-8 text-blue-600" />,
      title: "Team Collaboration",
      description: "We believe in the power of teamwork and collaborative problem-solving."
    },
    {
      icon: <Award className="w-8 h-8 text-blue-600" />,
      title: "Excellence",
      description: "We strive for excellence in everything we do, delivering quality results."
    },
    {
      icon: <Heart className="w-8 h-8 text-blue-600" />,
      title: "Work-Life Balance",
      description: "We promote a healthy work-life balance for all our team members."
    }
  ];

  const JobCard = ({ job, index }: { job: JobPosition; index: number }) => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        viewport={{ once: true }}
        className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow"
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold text-blue-700 mb-2">{job.title}</h3>
            <div className="flex items-center text-gray-600 text-sm space-x-4 mb-2">
              <span className="flex items-center">
                <MapPin className="w-4 h-4 mr-1" />
                {job.location}
              </span>
              <span className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {job.type}
              </span>
              <span className="flex items-center">
                <DollarSign className="w-4 h-4 mr-1" />
                {job.salary}
              </span>
            </div>
          </div>
          <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-medium">
            {job.department}
          </span>
        </div>
        
        <p className="text-gray-700 mb-4">{job.description}</p>
        
        <div className="mb-4">
          <h4 className="font-semibold text-gray-800 mb-2">Requirements:</h4>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            {job.requirements.map((req, idx) => (
              <li key={idx}>{req}</li>
            ))}
          </ul>
        </div>
        
        <div className="mb-6">
          <h4 className="font-semibold text-gray-800 mb-2">Benefits:</h4>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-1">
            {job.benefits.map((benefit, idx) => (
              <li key={idx}>{benefit}</li>
            ))}
          </ul>
        </div>
        
        <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium">
          Apply Now
        </button>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <meta name="description" content="Join DG IT team! Explore career opportunities in graphic design, video editing, content creation, and web development in Nepal." />
        <meta name="keywords" content="careers, jobs, graphic designer, video editor, content creator, web developer, Nepal" />
        <meta property="og:title" content="Careers - DG IT Pvt. Ltd." />
        <meta property="og:description" content="Join our dynamic team and build your career with us." />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <Navbar />
      <main className="flex-grow pt-24">
        <div className="container mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-800 mb-4">
              Join Our Team
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Be part of a dynamic, creative team that's passionate about delivering exceptional digital solutions.
            </p>
          </motion.div>

          {/* Company Values */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-blue-700 mb-8">Why Work With Us?</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {companyValues.map((value, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl shadow-lg p-6"
                  >
                    <div className="mb-4">{value.icon}</div>
                    <h3 className="text-xl font-bold text-blue-700 mb-2">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Current Openings */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-blue-700 mb-4">Current Openings</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Explore our current job opportunities and find the perfect role for your skills and career goals.
              </p>
            </motion.div>
            
            <div className="max-w-4xl mx-auto">
              {jobPositions.map((job, index) => (
                <JobCard key={index} job={job} index={index} />
              ))}
            </div>
          </section>

          {/* Application Process */}
          <section className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-50 rounded-2xl p-8"
            >
              <h2 className="text-3xl font-bold text-blue-700 mb-8 text-center">Application Process</h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {[
                  { step: "01", title: "Apply", desc: "Submit your application with resume and portfolio" },
                  { step: "02", title: "Review", desc: "Our team reviews your application and qualifications" },
                  { step: "03", title: "Interview", desc: "Selected candidates are invited for an interview" },
                  { step: "04", title: "Welcome", desc: "Successful candidates join our amazing team" }
                ].map((item, index) => (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                      {item.step}
                    </div>
                    <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </section>

          {/* Contact Section */}
          <section>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl p-10 text-white text-center"
            >
              <h2 className="text-3xl font-bold mb-4">Don't See a Perfect Match?</h2>
              <p className="text-xl text-blue-100 mb-6">
                We're always looking for talented individuals. Send us your resume and we'll consider you for future opportunities.
              </p>
              <a
                href="/contact"
                className="inline-block px-8 py-4 bg-white text-blue-600 font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Get in Touch
              </a>
            </motion.div>
          </section>
        </div>
      </main>
      <Footer />
      <BackToTopButton />
      <FloatingSocialButtons />
    </div>
  );
};

export default CareersPage;
